'use client';

import { Loader2 } from 'lucide-react';
import { Button, ButtonProps } from '@/app/_components/core/button';
import { toast } from '@/app/_components/core/toast';
import { useLabel } from '@/app/_contexts/labels-context';
import { useDownload } from '@/app/_hooks/use-download';
import { cn } from '@/app/_lib/utils';
import type { ReactNode } from 'react';

export interface DownloadButtonProps extends Omit<ButtonProps, 'onClick' | 'loading' | 'asChild' | 'onError'> {
  /** The URL to download from (should be the proxy URL from getDownloadUrl) */
  href: string;
  /** The filename for the downloaded file */
  filename: string;
  /** Button content */
  children: ReactNode;
  /** Callback when download succeeds */
  onSuccess?: () => void;
  /** Callback when download fails */
  onError?: (error: Error) => void;
  /** Passive, code-assigned tracking key rendered as `data-track-id`. */
  'data-track-id'?: string;
}

/**
 * DownloadButton - Client component for file downloads with loading state.
 *
 * Features:
 * - Shows loading spinner before text while downloading (button width stays constant)
 * - Text stays the same during loading, icon remains visible
 * - Returns to initial state on success, shows toast notification on error
 * - Supports all Button variants and sizes
 * - iOS Safari: Opens file in new tab (no toast since we can't confirm success)
 *
 * @example
 * ```tsx
 * <DownloadButton
 *   href={getDownloadUrl(fileUrl, filename)}
 *   filename="document.pdf"
 *   variant="outline"
 *   endIcon={<Download />}
 * >
 *   Download PDF
 * </DownloadButton>
 * ```
 */
export function DownloadButton({
  href,
  filename,
  children,
  onSuccess,
  onError,
  endIcon,
  className,
  disabled,
  'data-track-id': dataTrackId,
  ...buttonProps
}: DownloadButtonProps) {
  const successLabel = useLabel('downloadSuccess', 'Download erfolgreich');
  const errorLabel = useLabel('downloadError', 'Download fehlgeschlagen');

  const { isLoading, isIOSSafari, download } = useDownload({
    onSuccess: () => {
      // No toast for iOS Safari - we don't know if download actually succeeded
      if (!isIOSSafari) {
        toast.success(successLabel);
      }
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(errorLabel, {
        description: error.message,
      });
      onError?.(error);
    },
  });

  const handleClick = () => {
    if (!isLoading) {
      void download(href, filename);
    }
  };

  return (
    <Button
      {...buttonProps}
      className={cn('cursor-pointer', className)}
      loading={false}
      endIcon={endIcon}
      onClick={handleClick}
      disabled={disabled || isLoading}
      data-track-id={dataTrackId}
    >
      <span className="relative inline-flex min-w-0">
        {/* Original text - invisible during loading but keeps width */}
        <span className={cn(isLoading && 'invisible')}>{children}</span>
        {/* Loading overlay - spinner + same text, button width stays constant */}
        {isLoading && (
          <span className="absolute inset-0 flex min-w-0 items-center gap-2 overflow-hidden">
            <Loader2 className="size-4 shrink-0 animate-spin" />
            <span className="min-w-0 flex-1 truncate">{children}</span>
          </span>
        )}
      </span>
    </Button>
  );
}
